"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { sessionStore } from "@/utils/storage";

// Key used to pass a scroll intent across a route change into the
// About Us layout. Set by links that navigate from outside this layout.
export const ABOUT_SCROLL_INTENT_KEY = "aboutScrollIntent";

/**
 * Scrolls to the About Us main content section (skipping the hero),
 * offset by the fixed navbar so the section isn't hidden underneath.
 * Retries across animation frames until the section exists, then
 * re-asserts for a few frames to win against scroll restoration.
 */
export function scrollToAboutMainSection() {
  let waited = 0;
  let reasserts = 0;

  const scroll = () => {
    const mainSection = document.getElementById("about-main-section");
    const mainNav = document.querySelector("nav");
    const navHeight = mainNav ? mainNav.offsetHeight : 0;

    if (mainSection) {
      const top = mainSection.getBoundingClientRect().top + window.scrollY - navHeight;
      window.scrollTo({ top, left: 0 });
      if (reasserts < 12) {
        reasserts += 1;
        requestAnimationFrame(scroll);
      }
    } else if (waited < 90) {
      waited += 1;
      requestAnimationFrame(scroll);
    }
  };

  requestAnimationFrame(scroll);
}

/**
 * Runs inside the About Us layout. After the layout mounts (or the route
 * within the layout changes), it reads a scroll intent left by a link and
 * scrolls to the main content section, offset by the fixed navbar.
 *
 * This is more reliable than scrolling from the link handler because the
 * target section (#about-main-section) is guaranteed to exist here.
 */
export default function AboutUsScrollOnLoad() {
  const pathname = usePathname();

  useEffect(() => {
    const intent = sessionStore.get(ABOUT_SCROLL_INTENT_KEY);
    if (intent !== "main-section") return;

    sessionStore.remove(ABOUT_SCROLL_INTENT_KEY);
    scrollToAboutMainSection();
  }, [pathname]);

  return null;
}
